import type { CrewMember, Document } from '../types';

/**
 * Certificate type constants for validation.
 * Maps to the actual certificate type values stored in the database.
 */
export type CertificateType = 'sea_survival' | 'first_aid' | 'radio_operator';

/**
 * Document type mappings - maps certificate columns to document types.
 * These are the documentType values stored in uploaded documents.
 * Note: Database stores snake_case values like 'sea_survival_certificate'
 */
export const DOCUMENT_TYPE_MAP = {
  sssc: 'sea_survival_certificate',
  firstAid: 'first_aid_certificate',
  radio: 'radio_operator_certificate',
} as const;

/**
 * Alternative document types that should also match.
 * Used for backwards compatibility with older document types.
 */
export const DOCUMENT_TYPE_ALIASES: Record<string, string[]> = {
  sea_survival_certificate: ['sea_safety_certificate', 'offshore_safety_certificate', 'sea_safety'],
  first_aid_certificate: ['firstaid_certificate', 'first_aid'],
  radio_operator_certificate: ['radio_license', 'marine_radio_license', 'src_certificate'],
};

/**
 * Check if a crew member has a valid certificate of the specified type.
 * A certificate is valid if:
 * 1. The type matches the requested type
 * 2. The expiry date is in the future OR not set (no expiry)
 * 
 * Requirements: 5.1, 5.2, 5.3
 */
export function hasValidCertificate(
  crew: CrewMember,
  certType: CertificateType
): boolean {
  if (!crew.certificates || crew.certificates.length === 0) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return crew.certificates.some((cert) => {
    // Check type matches
    if (cert.type !== certType) {
      return false;
    }

    // Check expiry - valid if no expiry date or expiry is in the future
    if (!cert.expiryDate) {
      return true;
    }

    const expiryDate = new Date(cert.expiryDate);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate >= today;
  });
}

/**
 * Get certificate status for all three certificate types.
 * SSSC = Sea Survival Safety Certificate (sea_survival)
 * First Aid = first_aid
 * Radio = radio_operator
 */
export function getCertificateStatus(crew: CrewMember): {
  sssc: boolean;
  firstAid: boolean;
  radio: boolean;
} {
  return {
    sssc: hasValidCertificate(crew, 'sea_survival'),
    firstAid: hasValidCertificate(crew, 'first_aid'),
    radio: hasValidCertificate(crew, 'radio_operator'),
  };
}

/**
 * Certificate status for a single certificate type.
 * - 'valid': Document exists and is not expired
 * - 'expired': Document exists but is expired
 * - 'missing': No document of this type
 */
export type CertStatus = 'valid' | 'expired' | 'missing';

/**
 * Check if a crew member has a document of the specified type and its status.
 * Returns 'valid', 'expired', or 'missing'.
 */
export function getDocumentStatus(
  crewId: string,
  documentType: string,
  documents: Document[]
): CertStatus {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get all document types to check (primary + aliases)
  const typesToCheck = [documentType];
  const aliases = DOCUMENT_TYPE_ALIASES[documentType];
  if (aliases) {
    typesToCheck.push(...aliases);
  }

  // Find all documents of matching types for this crew member
  const matchingDocs = documents.filter(
    (doc) => doc.crewId === crewId && typesToCheck.includes(doc.documentType)
  );

  if (matchingDocs.length === 0) {
    return 'missing';
  }

  // Check if any are valid (not expired)
  const hasValid = matchingDocs.some((doc) => {
    if (!doc.expirationDate) {
      return true; // No expiry = valid
    }
    const expiryDate = new Date(doc.expirationDate);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate >= today;
  });

  return hasValid ? 'valid' : 'expired';
}

/**
 * Get certificate status for all three certificate types based on uploaded documents.
 * Returns status for each: 'valid', 'expired', or 'missing'
 */
export function getCertificateStatusFromDocuments(
  crewId: string,
  documents: Document[]
): {
  sssc: CertStatus;
  firstAid: CertStatus;
  radio: CertStatus;
} {
  return {
    sssc: getDocumentStatus(crewId, DOCUMENT_TYPE_MAP.sssc, documents),
    firstAid: getDocumentStatus(crewId, DOCUMENT_TYPE_MAP.firstAid, documents),
    radio: getDocumentStatus(crewId, DOCUMENT_TYPE_MAP.radio, documents),
  };
}
